
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../assets/images/arka-saydam-logo.png';

const AssistantTopBar = ({
    user,
    avatar,
    isDarkMode,
    toggleDarkMode,
    hasBaby,
    openModal,
    unreadCount,
    sharedListCount,
    logout
}) => {
    const navigate = useNavigate();
    const [profileOpen, setProfileOpen] = useState(false);
    const profileRef = useRef(null);

    // Close profile dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileRef.current && !profileRef.current.contains(event.target)) {
                setProfileOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <>
            {/* WEB VIEW HEADER */}
            <header className="hidden md:flex sticky top-0 z-[110] h-20 w-full items-center border-b border-gray-200 dark:border-slate-800 bg-white dark:bg-[#1a1a1a] shadow-sm transition-colors duration-200">
                <div className="w-full px-2 md:px-10 h-full">
                    {/* Aligned with DashboardLayout's max-w-5xl constraint for Assistant Chefs */}
                    <div className="mx-auto max-w-5xl flex items-center justify-between h-full">

                        {/* Left section: Logo & App Name */}
                        <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/partner-dashboard')}>
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white dark:bg-slate-800 shadow-sm ring-1 ring-black/5">
                                <img src={Logo} alt="Logo" className="h-8 w-8 object-contain" />
                            </div>
                            <div className="flex flex-col">
                                <h1 className="text-lg font-black leading-tight text-gray-900 dark:text-white">Bana Bir Tarif Ver</h1>
                                <p className="text-[10px] font-bold text-blue-500 uppercase tracking-widest">Mutfak Asistanı</p>
                            </div>
                        </div>

                        {/* Right section: Profile hub */}
                        <div className="flex items-center gap-4 lg:gap-6">
                            {/* Notification Bell (Linked to Shared Lists) */}
                            <button
                                onClick={() => navigate('/partner-dashboard')}
                                className="relative p-2 text-slate-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full transition-colors group"
                                title="Market Listeleri"
                            >
                                <span className="material-symbols-outlined text-[28px] group-hover:scale-110 transition-transform">shopping_bag</span>
                                {sharedListCount > 0 && (
                                    <span className="absolute top-1.5 right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-blue-500 text-[10px] font-bold text-white ring-2 ring-white dark:ring-slate-900 animate-pulse">
                                        {sharedListCount}
                                    </span>
                                )}
                            </button>

                            <div className="flex items-center gap-4">
                                <div className="flex flex-col items-end justify-center">
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-sm font-black text-slate-900 dark:text-white transition-colors">
                                            {user?.name?.split(' ')[0] || 'Asistan'} Şef
                                        </h2>
                                        <span className="rounded bg-blue-500 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-white shadow-sm">
                                            Asistan
                                        </span>
                                    </div>

                                    {/* --- Web View Icons (Below Name) --- */}
                                    <div className="flex flex-row-reverse items-center gap-1.5 mt-2">
                                        {/* 1. Dark Mode */}
                                        <button onClick={(e) => { e.stopPropagation(); toggleDarkMode(); }} className="size-8 bg-white dark:bg-slate-800 rounded-lg shadow-md flex items-center justify-center border border-gray-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer group" title="Tema Değiştir">
                                            <span className="material-symbols-outlined text-[18px] text-slate-500 dark:text-slate-400 group-hover:rotate-12 transition-transform">{isDarkMode ? 'light_mode' : 'dark_mode'}</span>
                                        </button>

                                        {/* 2. Settings */}
                                        <button onClick={(e) => { e.stopPropagation(); navigate('/profile'); }} className="size-8 bg-white dark:bg-slate-800 rounded-lg shadow-md flex items-center justify-center border border-gray-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer group" title="Ayarlar">
                                            <span className="material-symbols-outlined text-[18px] text-slate-500 dark:text-slate-400 group-hover:rotate-45 transition-transform">settings</span>
                                        </button>

                                        {/* 3. Shield (Kalkan) */}
                                        <button onClick={(e) => { e.stopPropagation(); openModal('kalkan'); }} className="size-8 bg-blue-500 rounded-lg shadow-md flex items-center justify-center border border-blue-400 hover:bg-blue-600 transition-colors cursor-pointer text-white group" title="Mutfak Güvenliği">
                                            <span className="material-symbols-outlined text-[18px] group-hover:scale-110 transition-transform">verified_user</span>
                                        </button>

                                        {/* 4. Baby (Left of Shield) */}
                                        {hasBaby && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); openModal('bebek'); }}
                                                className="size-8 bg-blue-500 rounded-lg shadow-md flex items-center justify-center border border-blue-400 hover:bg-blue-600 transition-colors cursor-pointer text-white group"
                                                title="Bebek Zırhı"
                                            >
                                                <span className="material-symbols-outlined text-[18px] group-hover:scale-110 transition-transform">child_care</span>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="relative group" ref={profileRef}>
                                    <div onClick={() => setProfileOpen(!profileOpen)} className="size-14 overflow-hidden rounded-2xl border-2 border-white dark:border-slate-800 shadow-md ring-2 ring-blue-500/20 transition-transform group-hover:scale-105 cursor-pointer z-[120] relative">
                                        <img alt="Şef Avatar" className="h-full w-full object-cover" src={avatar || `https://ui-avatars.com/api/?name=${user?.name || 'Asistan'}&background=blue&color=fff`} />
                                    </div>

                                    {/* Profile Menu */}
                                    <div className={`absolute right-0 top-full mt-4 w-56 origin-top-right rounded-2xl bg-white dark:bg-slate-800 shadow-xl ring-1 ring-black/5 p-2 flex flex-col gap-1 transition-all duration-150 transform z-[120] ${profileOpen ? 'opacity-100 visible scale-100' : 'opacity-0 invisible scale-95 pointer-events-none'}`}>
                                        <button onClick={() => { setProfileOpen(false); navigate('/profile'); }} className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                                            <i className="fa-solid fa-user text-[18px]"></i>
                                            Profili Düzenle
                                        </button>
                                        <div className="h-px bg-neutral-100 dark:bg-slate-700 mx-2" />
                                        <button onClick={async () => { setProfileOpen(false); await logout(); }} className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors">
                                            <i className="fa-solid fa-right-from-bracket text-[18px]"></i>
                                            Çıkış Yap
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* MOBILE VIEW HEADER */}
            <header className="flex md:hidden sticky top-0 z-[110] bg-white dark:bg-[#1a1a1a] shadow-md w-full transition-all flex-col border-b dark:border-slate-700">
                <div className="flex items-center justify-between px-3 h-[80px]">
                    {/* Left: App Logo/Icon */}
                    <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/partner-dashboard')}>
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white dark:bg-slate-800 shadow-sm ring-1 ring-black/5">
                            <img src={Logo} alt="Logo" className="h-9 w-9 object-contain" />
                        </div>
                    </div>

                    {/* Profile Section on Mobile */}
                    <div className="flex items-center gap-2 shrink-0">
                        {/* Notification Button (Shared Lists) */}
                        <button
                            onClick={() => navigate('/partner-dashboard')}
                            className="relative p-1.5 text-slate-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full transition-colors"
                        >
                            <span className="material-symbols-outlined text-[24px]">shopping_bag</span>
                            {sharedListCount > 0 && (
                                <span className="absolute top-0.5 right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-blue-500 text-[8px] font-black text-white ring-1 ring-white dark:ring-slate-900 animate-pulse">
                                    {sharedListCount}
                                </span>
                            )}
                        </button>

                        <div className="flex flex-col items-end justify-center gap-1.5">
                            <div className="flex items-center gap-1.5">
                                <h2 className="text-[10px] font-black text-slate-900 dark:text-white whitespace-nowrap uppercase">
                                    {user?.name?.split(' ')[0] || 'Asistan'} Şef
                                </h2>
                            </div>

                            {/* --- MOBILE ICON SEQUENCE --- */}
                            <div className="flex items-center gap-1">
                                {hasBaby && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); openModal('bebek'); }}
                                        className="size-7 bg-blue-500 rounded-lg shadow-sm flex items-center justify-center border border-blue-400 text-white active:scale-90 transition-transform"
                                    >
                                        <span className="material-symbols-outlined text-[14px]">child_care</span>
                                    </button>
                                )}
                                <button onClick={(e) => { e.stopPropagation(); openModal('kalkan'); }} className="size-7 bg-blue-500 rounded-lg shadow-sm flex items-center justify-center border border-blue-400 text-white active:scale-90 transition-transform">
                                    <span className="material-symbols-outlined text-[14px]">verified_user</span>
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); navigate('/profile'); }} className="size-7 bg-white dark:bg-slate-800 rounded-lg shadow-sm flex items-center justify-center border dark:border-slate-700">
                                    <span className="material-symbols-outlined text-[14px] text-slate-500 dark:text-slate-400">settings</span>
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); toggleDarkMode(); }} className="size-7 bg-white dark:bg-slate-800 rounded-lg shadow-sm flex items-center justify-center border dark:border-slate-700">
                                    <span className="material-symbols-outlined text-[14px] text-slate-500 dark:text-slate-400">{isDarkMode ? 'light_mode' : 'dark_mode'}</span>
                                </button>
                            </div>
                        </div>

                        {/* Avatar */}
                        <div className="relative shrink-0" ref={profileRef}>
                            <div onClick={() => setProfileOpen(!profileOpen)} className="size-12 overflow-hidden rounded-2xl border-2 border-white dark:border-slate-800 shadow-md ring-2 ring-blue-500/20 transition-transform cursor-pointer">
                                <img alt="Asistan Avatar" className="h-full w-full object-cover" src={avatar || `https://ui-avatars.com/api/?name=${user?.name || 'Asistan'}&background=blue&color=fff`} />
                            </div>

                            {/* Mobile Profile Menu */}
                            <div className={`absolute right-0 top-full mt-2 w-48 origin-top-right rounded-xl bg-white dark:bg-slate-800 shadow-xl ring-1 ring-black/5 p-2 flex flex-col gap-1 transition-all duration-150 transform z-[120] ${profileOpen ? 'opacity-100 visible scale-100' : 'opacity-0 invisible scale-95 pointer-events-none'}`}>
                                <button onClick={() => { setProfileOpen(false); navigate('/profile'); }} className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-slate-700">
                                    <i className="fa-solid fa-user"></i> Profili Düzenle
                                </button>
                                <div className="h-px bg-neutral-100 dark:bg-slate-700 mx-2" />
                                <button onClick={async () => { setProfileOpen(false); await logout(); }} className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">
                                    <i className="fa-solid fa-right-from-bracket"></i> Çıkış Yap
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </header>
        </>
    );
};

export default AssistantTopBar;
